/**
 * Pipeline Service
 *
 * API client for Pipeline Orchestrator endpoints.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export type ProcessingMode = 'auto' | 'batch' | 'realtime';

export interface PipelineConfig {
  pilot_category: string;
  pilot_product_count?: number;
  language?: string;
  max_iterations_per_product?: number;
  processing_mode?: ProcessingMode;  // auto (default): batch for >20 products; batch: always batch; realtime: always realtime
  validation_thresholds?: {
    persona_min_score?: number;
    coherence_min_score?: number;
  };
}

export interface PipelineRun {
  id: string;
  site_id: string;
  state: PipelineState;
  config: PipelineConfig;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  metrics?: PipelineMetrics;
}

export type PipelineState =
  | 'CONFIGURING'
  | 'PILOT_RUNNING'
  | 'PILOT_REVIEW'
  | 'PILOT_REJECTED'
  | 'FULL_RUNNING'
  | 'FULL_PAUSED'
  | 'IMPACT_ANALYSIS'
  | 'IMPACT_REVIEW'
  | 'PROPAGATION_RUNNING'
  | 'COMPLETED'
  | 'FAILED';

export interface PipelineMetrics {
  pilot_products?: number;
  total_products?: number;
  processed_products?: number;
  passed_products?: number;
  failed_products?: number;
  looped_products?: number;
  research_created?: number;
}

export interface AEOStatusCounts {
  draft: number;
  validated: number;
  needs_research: number;
  published: number;
  archived: number;
  total: number;
  pendingReview: number;  // draft + validated
  approved: number;       // published
  rejected: number;       // archived
}

export interface PipelineDashboard {
  run: PipelineRun;
  state: PipelineState;
  progress: {
    pilot?: PhaseProgress;
    full?: PhaseProgress;
    propagation?: PhaseProgress;
  };
  timing: {
    started_at?: string;
    pilot_completed_at?: string;
    full_completed_at?: string;
    completed_at?: string;
  };
  alerts: PipelineAlert[];
  total_cost_usd?: number;
  /** task-px4ret1um8: LLM calls the wrapper could not price; > 0 ⇒ total_cost_usd is a FLOOR, never a bare number */
  unmeasured_calls?: number;
  cost_measured?: boolean;
  aeoStatusCounts?: AEOStatusCounts;
}

export interface PhaseProgress {
  total: number;
  pending: number;
  enriching: number;
  generating: number;
  validating: number;
  researching: number;
  passed: number;
  failed: number;
  skipped: number;
  loop_detected: number;
}

export interface PipelineAlert {
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  productId?: string;
}

export interface ProductStatus {
  productId: string;
  generationId?: string;
  productName: string;
  productUrl?: string;
  brand?: string;
  category?: string;
  price?: string;
  imageUrl?: string;
  phase?: string;
  state: string;
  iterations: number;
  validation?: ValidationResults;
  validationResults?: ValidationResults;
  researchCreated?: ResearchEntry[];
  lastError?: string;
  started_at?: string;
  completed_at?: string;
}

export interface ValidationResults {
  passed: boolean;
  story49?: { decision: string; discrepancies?: number };
  story51?: {
    score: number;
    decision: string;
    mismatchReason?: string;
    recommendations?: string[];
  };
  story52?: { score: number; decision: string };
  story53?: {
    score: number;
    decision: string;
    missingCount?: number;
    missingSpecs?: Array<{ name: string; severity: string }>;
  };
  failures: Array<{ story: number | string; reason: string }>;
}

export interface ResearchEntry {
  id: string;
  title: string;
  type: string;
  createdAt: string;
}

export interface FactualDiscrepancy {
  attribute: string;
  generated: string;
  actual: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  feedDataQuality?: string;
  canAutoFix: boolean;
}

export interface MissingSpec {
  name: string;
  severity: string;
  canAutoResearch: boolean;
}

export interface AutoFixSummary {
  canAutoFix: boolean;
  autoFixableCount: number;
  canAutoResearch: boolean;
  autoResearchableCount: number;
}

export interface LoopDiagnosis {
  productId: string;
  productTitle: string;
  iterationCount: number;
  failurePattern: string[];
  rootCauses: string[];
  recommendations: Array<string | { description: string; action: string; priority: string }>;
  // Specific details for UI display and auto-fix
  factualDiscrepancies?: FactualDiscrepancy[];
  missingSpecs?: MissingSpec[];
  autoFixSummary?: AutoFixSummary;
}

export interface PropagationCandidate {
  product_id: string;
  product_title: string;
  similarity_score: number;
  impact_reason: string;
  category: string;
  brand?: string;
}

// API Functions

/**
 * Create a new pipeline run
 */
export async function createPipelineRun(
  siteId: string,
  config: PipelineConfig
): Promise<{ success: boolean; run: PipelineRun }> {
  const response = await authFetch('/api/pipeline/runs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ site_id: siteId, config }),
  });
  return response.json();
}

/**
 * List pipeline runs for a site
 */
export async function listPipelineRuns(
  siteId: string,
  options?: { state?: PipelineState; limit?: number; offset?: number }
): Promise<{ runs: PipelineRun[]; total: number }> {
  const params = new URLSearchParams({ site_id: siteId });
  if (options?.state) params.append('state', options.state);
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));

  const response = await authFetch(`/api/pipeline/runs?${params}`);
  return response.json();
}

/**
 * Get pipeline dashboard data
 */
export async function getPipelineDashboard(runId: string): Promise<PipelineDashboard> {
  const response = await authFetch(`/api/pipeline/runs/${runId}`);
  return response.json();
}

/**
 * Start pilot phase
 */
export async function startPilot(runId: string): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/start-pilot`, {
    method: 'POST',
  });
  return response.json();
}

/**
 * Approve pilot and start full generation
 */
export async function approvePilot(runId: string): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/approve-pilot`, {
    method: 'POST',
  });
  return response.json();
}

/**
 * Reject pilot with feedback
 */
export async function rejectPilot(
  runId: string,
  feedback: string
): Promise<{ success: boolean; state: PipelineState }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/reject-pilot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback }),
  });
  return response.json();
}

/**
 * Get pilot review data
 */
export async function getPilotReview(runId: string): Promise<{
  products: ProductStatus[];
  summary: { passed: number; failed: number; total: number };
}> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/pilot-review`);
  return response.json();
}

/**
 * Resume paused pipeline
 */
export async function resumePipeline(
  runId: string,
  options?: { skipProductId?: string; skipReason?: string }
): Promise<{ success: boolean; message: string; reason?: string }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options || {}),
  });
  return response.json();
}

/**
 * Get product statuses for a phase
 */
export async function getProductStatuses(
  runId: string,
  phase: 'pilot' | 'full' | 'propagation',
  options?: { state?: string; page?: number; limit?: number }
): Promise<{ products: ProductStatus[]; total: number; page: number; limit: number }> {
  const params = new URLSearchParams({ phase });
  if (options?.state) params.append('state', options.state);
  if (options?.page) params.append('page', String(options.page));
  if (options?.limit) params.append('limit', String(options.limit));

  const response = await authFetch(`/api/pipeline/runs/${runId}/products?${params}`);
  return response.json();
}

/**
 * Get looped products and stats
 */
export async function getLoops(runId: string): Promise<{
  products: ProductStatus[];
  stats: { total: number; byCategory: Record<string, number> };
}> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/loops`);
  return response.json();
}

/**
 * Diagnose loop for a product
 */
export async function diagnoseLoop(runId: string, productId: string): Promise<LoopDiagnosis> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/loops/${productId}/diagnosis`);
  return response.json();
}

/**
 * Skip a looped product
 */
export async function skipProduct(
  runId: string,
  productId: string,
  reason: string
): Promise<{ success: boolean }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/skip-product/${productId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return response.json();
}

/**
 * Retry a looped product
 */
export async function retryProduct(
  runId: string,
  productId: string,
  phase?: string
): Promise<{ success: boolean }> {
  // A product present in several phases of a run is AMBIGUOUS, and the backend now refuses it
  // rather than resetting an arbitrary row. Optional here on purpose: single-phase products (the
  // common case) are unchanged, and callers that do not pass a phase get HTTP 400 with
  // `availablePhases` listing the choices instead of a 500.
  const response = await authFetch(`/api/pipeline/runs/${runId}/retry-product/${productId}`, {
    method: 'POST',
    ...(phase ? { body: JSON.stringify({ phase }) } : {}),
  });
  const result = await response.json();
  if (!response.ok) {
    throw Object.assign(new Error(result.error || result.message || `Retry failed (HTTP ${response.status})`), {
      status: response.status,
      code: result.code,
      availablePhases: result.availablePhases,
    });
  }
  return result;
}

/**
 * Run impact analysis
 */
export async function analyzeImpact(runId: string): Promise<{
  impactedCount: number;
  categories: string[];
}> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/analyze-impact`, {
    method: 'POST',
  });
  return response.json();
}

/**
 * Get propagation candidates
 */
export async function getPropagationCandidates(
  runId: string,
  options?: { page?: number; limit?: number }
): Promise<{ candidates: PropagationCandidate[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.page) params.append('page', String(options.page));
  if (options?.limit) params.append('limit', String(options.limit));

  const response = await authFetch(`/api/pipeline/runs/${runId}/propagation-candidates?${params}`);
  return response.json();
}

/**
 * Approve propagation and start regeneration
 */
export async function approvePropagation(
  runId: string,
  options?: { excludeProducts?: string[]; includeProducts?: string[] }
): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/approve-propagation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options || {}),
  });
  return response.json();
}

/**
 * Skip propagation and complete pipeline
 */
export async function skipPropagation(runId: string): Promise<{ success: boolean; state: PipelineState }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/skip-propagation`, {
    method: 'POST',
  });
  return response.json();
}

/**
 * Get alerts for a run
 */
export async function getAlerts(runId: string): Promise<{ alerts: PipelineAlert[] }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/alerts`);
  return response.json();
}

/**
 * Get validation breakdown by story
 */
export async function getValidationBreakdown(runId: string): Promise<{
  story49: { passed: number; failed: number };
  story51: { passed: number; failed: number; avgScore: number };
  story52: { passed: number; failed: number; avgScore: number };
  story53: { passed: number; failed: number };
}> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/validation-breakdown`);
  return response.json();
}

/**
 * Cancel pipeline run
 */
export async function cancelPipeline(
  runId: string,
  reason?: string
): Promise<{ success: boolean; state: PipelineState }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return response.json();
}

/**
 * Get categories for a site (for pilot selection)
 */
export async function getSiteCategories(siteId: string): Promise<{ categories: string[] }> {
  const response = await authFetch(`/api/sites/${siteId}/categories`);
  return response.json();
}

/**
 * Delete a pipeline run
 */
export async function deletePipelineRun(runId: string): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`/api/pipeline/runs/${runId}`, {
    method: 'DELETE',
  });
  return response.json();
}

/**
 * Search categories from product feed
 */
export interface CategoryOption {
  name: string;
  productCount: number;
}

export async function searchPipelineCategories(
  siteId: string,
  search?: string
): Promise<{ success: boolean; categories: CategoryOption[] }> {
  const params = new URLSearchParams({ site_id: siteId });
  if (search) params.append('search', search);

  const response = await authFetch(`/api/pipeline/categories?${params}`);
  return response.json();
}
